import React, { useState, useEffect } from 'react';
import { EditableThresholdCellProps } from '../../types/bulkR2fTypes';

/**
 * Component for displaying and editing threshold values in the bulk R2F table
 */
export const EditableThresholdCell: React.FC<EditableThresholdCellProps> = ({
    value,
    onChange,
    disabled = false,
    hasPendingChange = false,
    placeholder = ''
}) => {
    const [inputValue, setInputValue] = useState<string>('');
    const [isEditing, setIsEditing] = useState(false);

    // Update input value when prop value changes
    useEffect(() => {
        setInputValue(value !== undefined ? value.toString() : '');
    }, [value]);

    const handleClick = () => {
        if (!disabled) {
            setIsEditing(true);
        }
    };

    const handleBlur = () => {
        setIsEditing(false);
        const numericValue = parseFloat(inputValue);
        
        // Validate input value
        if (isNaN(numericValue)) {
            // Reset to original value if not a valid number
            setInputValue(value !== undefined ? value.toString() : '');
            return;
        }
        
        // Business rule validation
        if (numericValue < 0) {
            console.warn('Threshold values cannot be negative');
            setInputValue(value !== undefined ? value.toString() : '');
            return;
        }
        
        // Only call onChange if value actually changed and is valid
        if (numericValue !== value) {
            onChange(numericValue);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            handleBlur();
        } else if (e.key === 'Escape') {
            setInputValue(value !== undefined ? value.toString() : '');
            setIsEditing(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setInputValue(e.target.value);
    };

    const displayValue = value !== undefined ? value.toString() : '';
    const isEmpty = !displayValue;

    return (
        <div className={`editable-threshold-cell ${hasPendingChange ? 'has-pending-change' : ''} ${disabled ? 'disabled' : ''}`}>
            {isEditing ? (
                <input
                    type="number"
                    value={inputValue}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDown}
                    className="threshold-input"
                    placeholder={placeholder}
                    autoFocus
                    min="0"
                    step="0.1"
                />
            ) : (
                <div 
                    className={`threshold-display ${isEmpty ? 'empty' : ''}`}
                    onClick={handleClick}
                    title={disabled ? 'Read-only' : 'Click to edit'}
                >
                    {isEmpty ? (
                        <span className="empty-placeholder">—</span>
                    ) : (
                        <span className="threshold-value">{displayValue}</span>
                    )}
                    {hasPendingChange && (
                        <span className="pending-indicator" title="Unsaved changes">●</span>
                    )}
                </div>
            )}
        </div>
    );
};